import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { HowIBWorksModal } from '../../../Profile/IntroducingBroker/HowIBWorksModal'
import { HowMultiTierWorksModal } from '../../../Profile/IntroducingBroker/HowMultiTierWorksModal'
import { Loading } from '../../../global/Loading/Loading'
import { Button } from '../../../global/button/Button'
import { CopyLink } from '../../../global/copyLink/CopyLink'
import { Modal } from '../../../global/modal/Modal'
import { CopyIcon } from '../../../icons/CopyIcon'
import { IBTestSubCategory } from '../../../model/TestSectionsDto'
import { useNavigate } from '../../../navigation/custom-react-router-dom'
import { Text, TextH1, TextH3 } from '../../../ui/Typography/Typography'
import { useIBReferralCodes } from '../../../utils/useIBReferralCodes'

import styles from './IBSignupThankYouPage.module.scss'

export const IBSignupThankYouPage = (): JSX.Element => {
  const { subCategoryId } = useParams<{ subCategoryId: IBTestSubCategory }>()
  const navigate = useNavigate()

  const { ibCode, mtCode, isLoading } = useIBReferralCodes()

  const [isHowIBWorksModalOpen, setHowIBWorksModalOpen] = useState(false)
  const [isHowMIBWorksModalOpen, setHowMIBWorksModalOpen] = useState(false)

  const navigateTo = () => navigate('/dashboard/introducing-broker/wallets')

  const { t } = useTranslation()

  const isMultiTier = subCategoryId === IBTestSubCategory.MultiTier

  const code = useMemo(() => {
    if (isMultiTier && mtCode) {
      return mtCode
    }
    if (ibCode) {
      return ibCode
    }
    return ''
  }, [mtCode, ibCode, isMultiTier])

  return (
    <div className={styles.ibSignupThankYouPage}>
      <div className={styles.wrapper}>
        <div className={styles.loadingWrapper}>
          {isHowIBWorksModalOpen && (
            <Modal
              closeModal={() => setHowIBWorksModalOpen(false)}
              render={() => (
                <HowIBWorksModal
                  ibCode={subCategoryId === IBTestSubCategory.MultiTier ? mtCode : ibCode}
                  onCancel={() => setHowIBWorksModalOpen(false)}
                />
              )}
            />
          )}
          {isHowMIBWorksModalOpen && (
            <Modal
              closeModal={() => setHowMIBWorksModalOpen(false)}
              render={({ closeModal }) => (
                <HowMultiTierWorksModal onCancel={closeModal} ibCode={mtCode} />
              )}
            />
          )}
          <div className={styles.feedbackWrapper}>
            <div className={styles.header}>
              <TextH1>{t('Thank You!')}</TextH1>
              <Text>
                {isMultiTier && mtCode
                  ? t('IB.You have become an Introducing Broker in Multi-tier scheme')
                  : t('IB.You have become an Introducing Broker!')}
              </Text>
              <Text>
                {t(
                  'IB.Invite traders to join Tickmill by using your personal link and get a permanent commission on every trade they make.'
                )}
              </Text>
            </div>
            <Loading isLoading={isLoading} showLoadingIcon>
              <div className={styles.codeField}>
                <Text>{t('IB.Your Referral Code') + ':'}</Text>
                <div className={styles.codeWrapper}>
                  <TextH3>{code}</TextH3>
                  <CopyLink value={code}>
                    <CopyIcon size={20} />
                  </CopyLink>
                </div>
              </div>
            </Loading>
            {ibCode && !isMultiTier && (
              <div>
                <span className='is-link' onClick={() => setHowIBWorksModalOpen(true)}>
                  {t('IB.How IB works?')}
                </span>
              </div>
            )}
            {mtCode && isMultiTier && (
              <div>
                <span className='is-link' onClick={() => setHowMIBWorksModalOpen(true)}>
                  {t('IB.MultiTier.How Multi-tier works')}?
                </span>
              </div>
            )}
            <Button.New
              className={styles.submitButton}
              type='button'
              appearance='secondary'
              size='M'
              fullWidth
              onClick={navigateTo}
            >
              {t('Ok')}
            </Button.New>
          </div>
        </div>
      </div>
    </div>
  )
}
